/**
 * PreviewDataGrid — bảng preview READ-ONLY theo template list page (/convert-table):
 * wrapper `app-dataframe` + header gradient `app-table-header-cell` + TableVirtuoso
 * (`components.Table` cấp width) + FilterBar 1 nút Search + per-column ▽ filter +
 * freeze cột định danh + format Access parity (DD-MMM-YYYY / #,##0.00) + sort.
 *
 * Dùng cho bảng "Check data in the below form before click SAVE" trong các dialog
 * batch Pay (Outright / PTBF) — data do BE compute trả về (read-only, không edit).
 * Bỏ column-visibility menu + persistence + pagination (không phù hợp preview tạm
 * trong dialog); virtualization vẫn xử lý được nhiều dòng.
 */
import { useMemo, useState, type CSSProperties, type ReactNode } from 'react'

import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import dayjs from 'dayjs'
import { TableVirtuoso } from 'react-virtuoso'

import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
  type ColumnLike,
} from '@/components/dataList'
import {
  getTableSortSuffix,
  sortTableRows,
  toggleTableSort,
  type TableSortState,
} from '@/utils/tableSort'

export interface PreviewColumn {
  key: string
  label: string
  align?: 'left' | 'right'
  width?: number
  kind?: 'text' | 'date' | 'number' | 'int'
  /** Override auto-detect filter kind. */
  filter?: ColumnFilterKind
  /** Freeze (sticky-left) cột định danh — chỉ nên set cho ĐÚNG 1 cột. */
  freeze?: boolean
  /** Formatter riêng (vd số 3 decimals) — mặc định theo `kind`. */
  format?: (value: unknown, row: Record<string, unknown>) => string
  /** Cộng tổng ở dòng Total (footer). */
  sum?: boolean
}

interface Props {
  columns: PreviewColumn[]
  rows: Record<string, unknown>[]
  emptyText?: string
  /** Chiều cao vùng bảng (override clamp viewport của .app-dataframe cho dialog). */
  height?: number
  /** Tiêu đề hiển thị trên hàng Filter (gộp header + filter thành 1 dòng). */
  title?: ReactNode
  /** Spinner cạnh title khi đang cập nhật (preview đang load). */
  loading?: boolean
  /** Slot phải trên hàng Filter (vd New Pending fields). */
  rightSlot?: ReactNode
  /** Có handler → hiện nút Refresh (cập nhật preview ngay, không đợi debounce). */
  onRefresh?: () => void
}

const DEFAULT_WIDTH = 120

function defaultFormat(value: unknown, kind: PreviewColumn['kind']): string {
  if (value === null || value === undefined || value === '') return ''
  if (kind === 'date') {
    const d = dayjs(value as string)
    return d.isValid() ? d.format('DD-MMM-YYYY') : String(value)
  }
  if (kind === 'int') {
    const n = Number(value)
    return Number.isFinite(n) ? String(Math.round(n)) : String(value)
  }
  if (kind === 'number') {
    const n = Number(value)
    return Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : String(value)
  }
  return String(value)
}

export default function PreviewDataGrid({
  columns,
  rows,
  emptyText = 'No data',
  height = 300,
  title,
  loading,
  rightSlot,
  onRefresh,
}: Props) {
  const [searchTerm, setSearchTerm] = useState('')
  const searchPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{ key: string; position: ColumnFilterAnchorPosition } | null>(null)
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)

  const searchableColumns = useMemo(() => columns.map((c) => ({ key: c.key })), [columns])
  const frozenKeys = useMemo(() => new Set(columns.filter((c) => c.freeze).map((c) => c.key)), [columns])
  const minTableWidth = useMemo(
    () => columns.reduce((sum, c) => sum + (c.width ?? DEFAULT_WIDTH), 0),
    [columns],
  )

  const filteredRows = useMemo(() => {
    const base = rows.filter(
      (r) =>
        rowMatchesSearch(r, searchTerm, searchableColumns) &&
        rowMatchesAllColumnFilters(r, columnFilters.filters),
    )
    return sortTableRows(base, sortState, columns as { key: string; kind?: never }[])
  }, [rows, searchTerm, searchableColumns, columnFilters.filters, sortState, columns])

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
  }

  const popoverColumn = useMemo(
    () => (colPopover ? columns.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover, columns],
  )
  const popoverKind = useMemo(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as ColumnLike, rows) : 'text'),
    [popoverColumn, rows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(rows, colPopover.key) : undefined,
    [popoverKind, colPopover, rows],
  )

  const cellText = (row: Record<string, unknown>, col: PreviewColumn): string =>
    col.format ? col.format(row[col.key], row) : defaultFormat(row[col.key], col.kind)

  const hasSum = columns.some((c) => c.sum)
  const sumTotals = useMemo(() => {
    const t: Record<string, number> = {}
    for (const c of columns) {
      if (!c.sum) continue
      let s = 0
      for (const r of filteredRows) {
        const n = Number(r[c.key])
        if (Number.isFinite(n)) s += n
      }
      t[c.key] = s
    }
    return t
  }, [columns, filteredRows])
  const footerText = (col: PreviewColumn): string =>
    col.format ? col.format(sumTotals[col.key], {}) : defaultFormat(sumTotals[col.key], col.kind)

  return (
    <>
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        showLabel={!title}
        rightSlot={rightSlot}
      >
        {(title || onRefresh) && (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mr: 1 }}>
            {title && <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>{title}</Typography>}
            {loading && <CircularProgress size={14} />}
            {onRefresh && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                onClick={onRefresh}
                disabled={loading}
                sx={{ fontSize: '0.7rem', py: 0.1, px: 0.75, minHeight: 24, textTransform: 'none' }}
              >
                Refresh
              </Button>
            )}
          </Stack>
        )}
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e) => searchPopover.open('search', e)}
        />
      </FilterBar>

      <FilterPopover
        open={searchPopover.state !== null}
        anchorEl={searchPopover.state?.anchor ?? null}
        onClose={searchPopover.close}
        maxWidth={320}
      >
        {searchPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search all columns…"
            label="Search"
            onClear={searchPopover.close}
          />
        )}
      </FilterPopover>

      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={() => setColPopover(null)}
        label={popoverColumn?.label ?? ''}
        kind={popoverKind === 'none' ? 'text' : popoverKind}
        value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
        onChange={(v) => colPopover && columnFilters.setFilter(colPopover.key, v)}
        options={popoverOptions}
      />

      <Box className="app-dataframe" sx={{ height, minHeight: 0 }}>
        <Box
          className="app-dataframe-toolbar"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, minHeight: 24, py: 0 }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', whiteSpace: 'nowrap', mr: 0.5 }}>
            Total: {filteredRows.length}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {filteredRows.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                {hasActiveFilters ? 'No rows match the filters' : emptyText}
              </Typography>
            </Stack>
          ) : (
            <TableVirtuoso
              key={filteredRows.length}
              style={{ height: '100%' }}
              data={filteredRows}
              fixedHeaderContent={() => (
                <TableRow>
                  {columns.map((col) => {
                    const isFrozen = frozenKeys.has(col.key)
                    const w = col.width ?? DEFAULT_WIDTH
                    const headerStyle: CSSProperties = { width: w, minWidth: w, maxWidth: w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => setSortState((prev) => toggleTableSort(prev, col.key))}
                        style={headerStyle}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          userSelect: 'text',
                          ...(isFrozen ? {} : { position: 'relative' }),
                        }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(col.key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={
                hasSum
                  ? () => (
                      <TableRow>
                        {columns.map((col, i) => {
                          const isFrozen = frozenKeys.has(col.key)
                          const w = col.width ?? DEFAULT_WIDTH
                          const val = col.sum ? footerText(col) : i === 0 ? 'Total' : ''
                          return (
                            <TableCell
                              key={col.key}
                              align={col.align}
                              className={isFrozen ? 'app-sticky-col' : undefined}
                              style={{ width: w, minWidth: w, maxWidth: w }}
                              sx={{
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                py: 0.5,
                                bgcolor: 'background.paper',
                                borderTop: (theme) => `2px solid ${theme.palette.divider}`,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {val}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    )
                  : undefined
              }
              itemContent={(_index: number, row: Record<string, unknown>) =>
                columns.map((col) => {
                  const isFrozen = frozenKeys.has(col.key)
                  const w = col.width ?? DEFAULT_WIDTH
                  const val = cellText(row, col)
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={{ width: w, minWidth: w, maxWidth: w }}
                      sx={{
                        whiteSpace: 'nowrap',
                        py: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        verticalAlign: 'middle',
                      }}
                    >
                      <Box
                        component="span"
                        className="app-table-cell-text"
                        title={val || '-'}
                        sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                Table: (props: any) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as never,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as never,
              }}
            />
          )}
        </Box>
      </Box>
    </>
  )
}
